/**
 * Front-my-End — Preview run policy (pure state, no DOM).
 *
 * Decides *what* a preview run renders and tracks the state that keeps
 * Live mode safe. Owns:
 * - the last explicitly-run JavaScript (`ranJs`): implicit runs (Live, theme
 *   switch, boot restore, preview navigation, save) never execute editor JS the
 *   user has not run on purpose;
 * - what was baked into the latest document, for the CSS hot-swap fast path;
 * - render generations, so a slow build can never overwrite a newer one;
 * - per-render CSS, so the preview can be re-synced after a render lands;
 * - the safe-boot flag: a render that never completed is not auto-run again on
 *   the next load (it may contain an infinite loop);
 * - the last reported preview scroll, restored after implicit re-renders.
 *
 * Storage is injected so the policy runs under `node:test`.
 */

export const RUN_TRIGGERS = Object.freeze({
  EXPLICIT: 'explicit',
  IMPLICIT: 'implicit'
});

export const RAN_JS_KEY = 'front_my_end_ran_js';
export const RENDER_IN_FLIGHT_KEY = 'front_my_end_render_in_flight';

const MAX_TRACKED_RENDERS = 8;

/**
 * FNV-1a over UTF-16 code units. Identifies a workspace for the safe-boot
 * flag without storing a second copy of the code.
 */
export function hashWorkspace(html = '', js = '') {
  const text = `${html}\u0000${js}`;
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `${text.length.toString(36)}-${(hash >>> 0).toString(36)}`;
}

function createLocalStorageAdapter() {
  return {
    get(key) {
      try {
        return localStorage.getItem(key);
      } catch (_e) {
        return null;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (_e) {
        /* Storage may be unavailable (private mode / quota). Fail soft. */
      }
    },
    remove(key) {
      try {
        localStorage.removeItem(key);
      } catch (_e) {
        /* Storage may be unavailable. Fail soft. */
      }
    }
  };
}

function clampScroll(value) {
  return Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
}

export function createRunPolicy({ storage = createLocalStorageAdapter(), now = () => Date.now() } = {}) {
  let ranJs = storage.get(RAN_JS_KEY);
  let baked = null;
  let renderGeneration = 0;
  let latestPostedTimingId = null;
  const renders = new Map();
  let scroll = { x: 0, y: 0 };

  function readInFlight() {
    const raw = storage.get(RENDER_IN_FLIGHT_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed.hash === 'string' ? parsed : null;
    } catch (_e) {
      return null;
    }
  }

  function forgetOldRenders() {
    while (renders.size > MAX_TRACKED_RENDERS) {
      renders.delete(renders.keys().next().value);
    }
  }

  return {
    /** JS the user last ran on purpose, or null when there is no record. */
    getRanJs() {
      return ranJs;
    },

    /** True when the editor JS differs from what was last run explicitly. */
    isJsPending(currentJs = '') {
      return (ranJs ?? '') !== (currentJs ?? '');
    },

    /**
     * Plans a run. Explicit runs execute the editor JS and record it as run;
     * implicit runs reuse the last-run JS. A CSS hot-swap is only chosen when
     * the caller allows it and nothing else baked into the document changed.
     */
    planRun({ trigger = RUN_TRIGGERS.IMPLICIT, html = '', js = '', theme = '', activePath = '', allowHotSwap = false } = {}) {
      const explicit = trigger === RUN_TRIGGERS.EXPLICIT;
      const effectiveJs = explicit ? js : (ranJs ?? '');
      if (explicit && ranJs !== js) {
        ranJs = js;
        storage.set(RAN_JS_KEY, js);
      }

      const hotSwap = Boolean(
        allowHotSwap &&
        !explicit &&
        baked &&
        baked.html === html &&
        baked.js === effectiveJs &&
        baked.theme === theme &&
        baked.activePath === activePath
      );

      if (hotSwap) {
        return { mode: 'style', effectiveJs, generation: renderGeneration };
      }

      renderGeneration += 1;
      baked = { html, js: effectiveJs, theme, activePath };
      return { mode: 'render', effectiveJs, generation: renderGeneration };
    },

    /** A newer render has started since `generation`; its build must be dropped. */
    isStale(generation) {
      return generation !== renderGeneration;
    },

    /** The document build failed; force the next run to rebuild fully. */
    invalidateBaked() {
      baked = null;
    },

    /** Records what a render was built with, before it is posted or queued. */
    recordRender(timingId, { css = '', hash = '' } = {}) {
      if (timingId === undefined || timingId === null) return;
      renders.set(timingId, { css, hash });
      forgetOldRenders();
    },

    /** A render left for the sandbox: raise the safe-boot flag for it. */
    markRenderPosted(timingId) {
      const render = renders.get(timingId);
      if (!render) return;
      latestPostedTimingId = timingId;
      storage.set(RENDER_IN_FLIGHT_KEY, JSON.stringify({ hash: render.hash, at: now() }));
    },

    /**
     * The sandbox finished a render. Returns the CSS to re-send when the
     * editor CSS moved on while this render was in flight, otherwise null.
     * Only the latest posted render clears the flag or re-syncs.
     */
    completeRender(timingId, currentCss = '') {
      if (timingId === undefined || timingId === null || timingId !== latestPostedTimingId) {
        return null;
      }
      const render = renders.get(timingId);
      storage.remove(RENDER_IN_FLIGHT_KEY);
      if (!render) return null;
      return render.css !== currentCss ? currentCss : null;
    },

    /** No render can complete (preview unavailable); never leave a false flag. */
    clearRenderInFlight() {
      latestPostedTimingId = null;
      storage.remove(RENDER_IN_FLIGHT_KEY);
    },

    /** True when a render of exactly this workspace started and never finished. */
    isUnfinishedWorkspace(hash) {
      const flag = readInFlight();
      return Boolean(flag && flag.hash === hash);
    },

    setScroll(x, y) {
      scroll = { x: clampScroll(x), y: clampScroll(y) };
    },

    resetScroll() {
      scroll = { x: 0, y: 0 };
    },

    getScroll() {
      return { ...scroll };
    }
  };
}
